import { createError } from 'h3'
import type { FetchError } from 'ofetch'
import type { ApiRes, HeadersOptionType, MethodType, ApiClientType } from '@/uTypes/api'
import { useApiUtils } from '@/uComposables/apiClient/useApiUtils'

/**
 * 지정된 기본 URL과 헤더 옵션으로 API 클라이언트를 생성하는 함수
 *
 * 생성된 클라이언트는 HTTP 메서드(GET, POST, PUT, PATCH, DELETE)에 대한 함수를 제공하며,
 * 각 요청에 인증 토큰을 자동으로 포함하고 오류 처리를 일관되게 수행함
 *
 * @param apiBaseUrl - API 요청의 기본 URL
 * @param headersOption - 모든 요청에 포함될 추가 헤더 (선택 사항)
 * @returns 다양한 HTTP 메서드를 지원하는 API 클라이언트 객체
 */
export const useApiClient = (
  apiBaseUrl: string,
  headersOption: HeadersOptionType = {}
): ApiClientType => {
  const { getToken, getErrorMsgByStatusCode, handleCSRError } = useApiUtils()

  const request = async <
    Res = unknown,
    Data extends Record<string, any> | null = null,
    Params extends Record<string, any> = {}
  >(
    method: MethodType,
    url: string,
    data?: Data,
    params?: Params
  ): Promise<ApiRes<Res>> => {
    const token = getToken()

    const options: any = {
      method,
      baseURL: apiBaseUrl,
      params,
      credentials: 'include',
      timeout: 20000, // 20초
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headersOption ?? {})
      }
    }
    if (method !== 'GET') options.body = data ?? null

    try {
      return await $fetch<ApiRes<Res>>(url, options)
    } catch (error: unknown) {
      const err = error as FetchError

      // 서버 에러 핸들링
      if (import.meta.server) {
        /**
         * ssr 에서는 알럿 노출이 불가능하기 때문에 페이지로 에러를 던지고 페이지에서 에러 메시지 노출해야함.
         * ssr, csr 에러 형태는 다르지만 페이지단에서 명확한 구분을 위해 메시지에 'from server-' 추가
         */
        const errMsg = 'from server-' + getErrorMsgByStatusCode(err?.response?.status ?? null, err)
        throw createError({
          statusCode: err.response?.status || 500,
          message: errMsg
        })
      }

      // 클라이언트 에러 핸들링
      handleCSRError(err)
      return Promise.reject(err.data)
    }
  }

  const get = <Res, Params extends Record<string, any> = {}>(
    url: string,
    params?: Params
  ): Promise<ApiRes<Res>> => {
    return request<Res, null, Params>('GET', url, null, params)
  }

  const post = <Res, Data extends Record<string, any> = {}>(
    url: string,
    data?: Data
  ): Promise<ApiRes<Res>> => {
    return request<Res, Data>('POST', url, data)
  }

  const put = <Res, Data extends Record<string, any> = {}>(
    url: string,
    data?: Data
  ): Promise<ApiRes<Res>> => {
    return request<Res, Data>('PUT', url, data)
  }

  const patch = <Res, Data extends Record<string, any> = {}>(
    url: string,
    data?: Data
  ): Promise<ApiRes<Res>> => {
    return request<Res, Data>('PATCH', url, data)
  }

  const del = <Res, Data extends Record<string, any> = {}>(
    url: string,
    data?: Data
  ): Promise<ApiRes<Res>> => {
    return request<Res, Data>('DELETE', url, data)
  }

  return {
    get,
    post,
    put,
    patch,
    delete: del // delete는 예약어라 del로 우회
  }
}
